import {
  apiCalls,
  INSPECTOR_PORTAL_QUERY_KEYS,
  useGetInspectorPortalAssignments,
} from "@/api/inspectorPortalQueries";
import { Card, CardContent } from "@/components/ui/card";
import DataTable from "@/components/DataTable/DataTable";
import { Input } from "@/components/ui/input";
import { useAuthSession } from "@/contexts/AuthSessionContext";
import useCustomSearchParams, {
  SearchParams,
} from "@/hooks/useCustomSearchParams";
import {
  InspectorPortalAssignmentSummary,
  ViewingsDispatchRequest,
} from "@/schemas";
import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Navigate } from "react-router-dom";
import { APP_ROUTES } from "@/config/appConfig";
import InspectorGroupedFilterPopover, {
  EMPTY_INSPECTOR_FILTERS,
  type DistanceFilter,
  type InspectorFilterState,
} from "./components/InspectorGroupedFilterPopover";
import {
  acceptOffer as acceptOfferLocally,
  declineAndRedispatch,
  submitViewingDate as submitViewingDateLocally,
} from "./dispatchActions";
import ViewingRequestCard from "./components/ViewingRequestCard";
import ViewingDetailSheet, {
  AssignmentDetailTab,
} from "./components/ViewingDetailSheet";
import { InspectorPortalSnapshot, RequestUpdater } from "./types";
import {
  clonePortalData,
  todayDateString,
  toViewingRequest,
  updateAssignmentFromRequest,
} from "./utils";
import { getViewingColumns } from "./viewingColumns";
import { Search } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";

const ASSIGNMENTS_QUERY = {
  limit: 50,
  offset: 0,
  sortBy: "statusDate" as const,
  sortOrder: "asc" as const,
};

const PENDING_OFFER_STATUSES = ["contacted"];
const MY_JOB_STATUSES = ["accepted", "scheduled"];
const PAST_JOB_STATUSES = ["completed", "cancelled"];
const INSPECTOR_HIDDEN_STATUSES = new Set<string>([
  "needsAction",
  "declined",
  "onHold",
  "completedRemotely",
]);

function isInspectorVisibleAssignment(
  assignment: InspectorPortalAssignmentSummary,
) {
  return !INSPECTOR_HIDDEN_STATUSES.has(assignment.lifecycleStatus);
}

type ViewingsTab = "pending" | "jobs" | "past";

const URL_PARAM_ASSIGNMENT_TAB = "assignmentTab";
const URL_PARAM_ASSIGNMENT_ID = "assignmentId";
const URL_PARAM_ASSIGNMENT_DETAIL_TAB = "assignmentDetailTab";
const VIEWINGS_TABS: ViewingsTab[] = ["pending", "jobs", "past"];
const DETAIL_TABS: AssignmentDetailTab[] = ["info", "files"];
const ACTION_ERROR_MESSAGE_IDS = {
  network: "actions.responseNetworkError",
  staleVersion: "actions.responseStaleVersionError",
  invalidTransition: "actions.responseInvalidTransitionError",
  unavailable: "viewings.detail.unavailable",
} as const;

const TAB_IDS: Record<ViewingsTab, string> = {
  pending: "viewings-tab-pending",
  jobs: "viewings-tab-jobs",
  past: "viewings-tab-past",
};
const TAB_PANEL_IDS: Record<ViewingsTab, string> = {
  pending: "viewings-tabpanel-pending",
  jobs: "viewings-tabpanel-jobs",
  past: "viewings-tabpanel-past",
};

function matchesDistanceFilter(
  distanceKm: number | undefined,
  filters: DistanceFilter[],
) {
  if (filters.length === 0) return true;
  if (distanceKm === undefined) return false;

  return filters.some((filter) => {
    if (filter === "under5") return distanceKm < 5;
    if (filter === "5to10") return distanceKm >= 5 && distanceKm <= 10;
    if (filter === "10to15") return distanceKm > 10 && distanceKm <= 15;
    return distanceKm > 15;
  });
}

function matchesInspectorFilters(
  request: ViewingsDispatchRequest,
  filters: InspectorFilterState,
) {
  if (
    filters.statuses.length > 0 &&
    !filters.statuses.includes(request.lifecycleStatus)
  ) {
    return false;
  }

  if (
    filters.inspectionTypes.length > 0 &&
    (!request.inspectionType ||
      !filters.inspectionTypes.includes(request.inspectionType))
  ) {
    return false;
  }

  if (
    filters.products.length > 0 &&
    (!request.productType || !filters.products.includes(request.productType))
  ) {
    return false;
  }

  if (!matchesDistanceFilter(request.distanceKm, filters.distances)) {
    return false;
  }

  if (
    filters.alerts.includes("waitingOver48h") &&
    !request.flags.waitingOver48h
  ) {
    return false;
  }

  return true;
}

function uniqueSorted<T extends string>(values: (T | undefined)[]) {
  return Array.from(new Set(values.filter(Boolean) as T[])).sort((a, b) =>
    a.localeCompare(b),
  );
}

function isViewingsTab(value: string | undefined): value is ViewingsTab {
  return Boolean(value && VIEWINGS_TABS.includes(value as ViewingsTab));
}

function isAssignmentDetailTab(
  value: string | undefined,
): value is AssignmentDetailTab {
  return Boolean(value && DETAIL_TABS.includes(value as AssignmentDetailTab));
}

function getTabForStatus(status: string): ViewingsTab {
  if (PENDING_OFFER_STATUSES.includes(status)) return "pending";
  if (MY_JOB_STATUSES.includes(status)) return "jobs";
  return "past";
}

function getActionErrorMessageId(error: unknown) {
  if (!isAxiosError(error)) return ACTION_ERROR_MESSAGE_IDS.network;

  const responseData = error.response?.data as
    | { code?: string; errorCode?: string }
    | undefined;
  const code = responseData?.code || responseData?.errorCode;

  if (code === "VIEWINGS_DISPATCH_STALE_VERSION") {
    return ACTION_ERROR_MESSAGE_IDS.staleVersion;
  }

  if (code === "VIEWINGS_DISPATCH_INVALID_TRANSITION") {
    return ACTION_ERROR_MESSAGE_IDS.invalidTransition;
  }

  if ([403, 404].includes(error.response?.status ?? 0)) {
    return ACTION_ERROR_MESSAGE_IDS.unavailable;
  }

  return ACTION_ERROR_MESSAGE_IDS.network;
}

export default function InspectorPortalPage() {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { getSearchParams, updateSearchParams } = useCustomSearchParams();
  const initialSearchParams = getSearchParams();
  const { session } = useAuthSession();
  const { data, isLoading, isError } =
    useGetInspectorPortalAssignments(ASSIGNMENTS_QUERY);
  const [snapshot, setSnapshot] = useState<InspectorPortalSnapshot>(() =>
    clonePortalData({ assignments: [] }),
  );
  const [search, setSearch] = useState("");
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(
    initialSearchParams[URL_PARAM_ASSIGNMENT_ID] || null,
  );
  const [activeTab, setActiveTab] = useState<ViewingsTab>(
    isViewingsTab(initialSearchParams[URL_PARAM_ASSIGNMENT_TAB])
      ? initialSearchParams[URL_PARAM_ASSIGNMENT_TAB]
      : "pending",
  );
  const [filters, setFilters] = useState<InspectorFilterState>(
    EMPTY_INSPECTOR_FILTERS,
  );
  const [acceptDateInput, setAcceptDateInput] = useState(todayDateString());
  const [uploadProgressByRequestId, setUploadProgressByRequestId] = useState<
    Record<string, number>
  >({});
  const [actionErrorMessageId, setActionErrorMessageId] = useState<
    string | null
  >(null);
  const pendingTabRef = useRef<HTMLButtonElement>(null);
  const jobsTabRef = useRef<HTMLButtonElement>(null);
  const pastTabRef = useRef<HTMLButtonElement>(null);
  const closingAssignmentIdRef = useRef<string | null>(null);
  const searchParams = getSearchParams();
  const activeDetailTab = isAssignmentDetailTab(
    searchParams[URL_PARAM_ASSIGNMENT_DETAIL_TAB],
  )
    ? searchParams[URL_PARAM_ASSIGNMENT_DETAIL_TAB]
    : "info";
  const updatePortalSearchParams = useCallback(
    (
      updates: Record<string, string | undefined>,
      options?: { replace?: boolean },
    ) => {
      const nextParams: SearchParams = { ...getSearchParams() };
      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined || value === "") {
          delete nextParams[key];
        } else {
          nextParams[key] = value;
        }
      });
      updateSearchParams(nextParams, options);
    },
    [getSearchParams, updateSearchParams],
  );
  const activeInspector = useMemo(
    () =>
      session
        ? {
            id: session.inspectorId,
            name: session.fullName ?? "Inspector",
            phone: "",
            isOnVacation: false,
          }
        : undefined,
    [session],
  );
  const updateRequest = useCallback(
    (requestId: string, updater: RequestUpdater) => {
      setSnapshot((current) => ({
        ...current,
        assignments: current.assignments.map((assignment) =>
          assignment.id === requestId
            ? updateAssignmentFromRequest(
                assignment,
                updater(
                  toViewingRequest(assignment, session?.inspectorId ?? ""),
                ),
              )
            : assignment,
        ),
      }));
    },
    [session?.inspectorId],
  );
  const replaceAssignment = useCallback(
    (assignment: InspectorPortalAssignmentSummary) => {
      setSnapshot((current) => ({
        ...current,
        assignments: current.assignments.map((currentAssignment) =>
          currentAssignment.id === assignment.id
            ? { ...currentAssignment, ...assignment }
            : currentAssignment,
        ),
      }));
    },
    [],
  );
  const handleAcceptOffer = useCallback(
    async (request: ViewingsDispatchRequest & { version?: number }) => {
      setActionErrorMessageId(null);
      if (activeInspector) {
        updateRequest(request.id, (current) =>
          acceptOfferLocally(current, activeInspector.name),
        );
      }

      try {
        const response = await apiCalls.acceptInspectorPortalAssignment(
          request.id,
          { version: request.version },
        );
        replaceAssignment(response.data.assignment);
      } catch (error) {
        updateRequest(request.id, () => request);
        setActionErrorMessageId(getActionErrorMessageId(error));
      } finally {
        await queryClient.invalidateQueries({
          queryKey: INSPECTOR_PORTAL_QUERY_KEYS.ASSIGNMENTS(),
        });
      }
    },
    [activeInspector, queryClient, replaceAssignment, updateRequest],
  );
  const handleDeclineOffer = useCallback(
    async (request: ViewingsDispatchRequest & { version?: number }) => {
      setActionErrorMessageId(null);
      if (activeInspector) {
        updateRequest(request.id, (current) =>
          declineAndRedispatch(current, activeInspector.name),
        );
        setSelectedRequestId(null);
      }

      try {
        const response = await apiCalls.declineInspectorPortalAssignment(
          request.id,
          { version: request.version },
        );
        replaceAssignment(response.data.assignment);
      } catch (error) {
        updateRequest(request.id, () => request);
        setSelectedRequestId(request.id);
        setActionErrorMessageId(getActionErrorMessageId(error));
      } finally {
        await queryClient.invalidateQueries({
          queryKey: INSPECTOR_PORTAL_QUERY_KEYS.ASSIGNMENTS(),
        });
      }
    },
    [activeInspector, queryClient, replaceAssignment, updateRequest],
  );
  const handleSubmitViewingDate = useCallback(
    async (
      request: ViewingsDispatchRequest & { version?: number },
      scheduledFor: string,
    ) => {
      if (activeInspector) {
        updateRequest(request.id, (current) =>
          submitViewingDateLocally(current, activeInspector.name, scheduledFor),
        );
      }

      const response = await apiCalls.scheduleInspectorPortalAssignment(
        request.id,
        { version: request.version, scheduledFor },
      );
      replaceAssignment(response.data.assignment);
      await queryClient.invalidateQueries({
        queryKey: INSPECTOR_PORTAL_QUERY_KEYS.ASSIGNMENTS(),
      });
    },
    [activeInspector, queryClient, replaceAssignment, updateRequest],
  );

  useEffect(() => {
    if (!data) return;

    setSnapshot(clonePortalData(data.data));
  }, [data]);

  useEffect(() => {
    const params = getSearchParams();
    const nextParams: SearchParams = { ...params };
    let shouldReplaceParams = false;
    const urlTab = params[URL_PARAM_ASSIGNMENT_TAB];
    const parsedTab = isViewingsTab(urlTab) ? urlTab : undefined;
    const urlAssignmentId = params[URL_PARAM_ASSIGNMENT_ID];
    const selectedAssignment = urlAssignmentId
      ? snapshot.assignments.find(
          (assignment) =>
            assignment.id === urlAssignmentId &&
            isInspectorVisibleAssignment(assignment),
        )
      : undefined;

    if (!urlAssignmentId) {
      closingAssignmentIdRef.current = null;
    }

    if (urlTab && !parsedTab) {
      delete nextParams[URL_PARAM_ASSIGNMENT_TAB];
      shouldReplaceParams = true;
    }

    if (parsedTab && !selectedAssignment && activeTab !== parsedTab) {
      setActiveTab(parsedTab);
    }

    if (urlAssignmentId && snapshot.assignments.length > 0) {
      if (closingAssignmentIdRef.current === urlAssignmentId) {
        return;
      }

      if (selectedAssignment) {
        if (selectedRequestId !== urlAssignmentId) {
          setSelectedRequestId(urlAssignmentId);
        }

        const selectedTab = getTabForStatus(selectedAssignment.lifecycleStatus);
        if (activeTab !== selectedTab) {
          setActiveTab(selectedTab);
        }
      } else {
        delete nextParams[URL_PARAM_ASSIGNMENT_ID];
        delete nextParams[URL_PARAM_ASSIGNMENT_DETAIL_TAB];
        shouldReplaceParams = true;
        if (selectedRequestId === urlAssignmentId) {
          setSelectedRequestId(null);
        }
      }
    }

    const urlDetailTab = params[URL_PARAM_ASSIGNMENT_DETAIL_TAB];
    if (
      urlDetailTab &&
      (!isAssignmentDetailTab(urlDetailTab) || !selectedAssignment)
    ) {
      delete nextParams[URL_PARAM_ASSIGNMENT_DETAIL_TAB];
      shouldReplaceParams = true;
    }

    if (shouldReplaceParams) {
      updateSearchParams(nextParams, { replace: true });
    }
  }, [
    activeTab,
    getSearchParams,
    selectedRequestId,
    snapshot.assignments,
    updateSearchParams,
  ]);

  const columns = useMemo(
    () =>
      getViewingColumns(intl, {
        activeInspector,
        onRespond: (request) => {
          setSelectedRequestId(request.id);
          updatePortalSearchParams({
            [URL_PARAM_ASSIGNMENT_TAB]: getTabForStatus(
              request.lifecycleStatus,
            ),
            [URL_PARAM_ASSIGNMENT_ID]: request.id,
            [URL_PARAM_ASSIGNMENT_DETAIL_TAB]: undefined,
          });
        },
      }),
    [activeInspector, intl, updatePortalSearchParams],
  );

  const handleUploadFiles = useCallback(
    async (requestId: string, files: File[]) => {
      setUploadProgressByRequestId((current) => ({
        ...current,
        [requestId]: 20,
      }));
      const response = await apiCalls.uploadInspectorPortalAssignmentFiles(
        requestId,
        files,
      );
      setUploadProgressByRequestId((current) => ({
        ...current,
        [requestId]: 100,
      }));
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: INSPECTOR_PORTAL_QUERY_KEYS.ASSIGNMENTS(),
        }),
        queryClient.invalidateQueries({
          queryKey: INSPECTOR_PORTAL_QUERY_KEYS.ASSIGNMENT_DETAIL(requestId),
        }),
      ]);

      return response.data.files.length;
    },
    [queryClient],
  );
  const handleDeleteFile = useCallback(
    async (requestId: string, fileId: number) => {
      await apiCalls.deleteInspectorPortalAssignmentFile(requestId, fileId);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: INSPECTOR_PORTAL_QUERY_KEYS.ASSIGNMENTS(),
        }),
        queryClient.invalidateQueries({
          queryKey: INSPECTOR_PORTAL_QUERY_KEYS.ASSIGNMENT_DETAIL(requestId),
        }),
      ]);
    },
    [queryClient],
  );

  if (!session) {
    return <Navigate to={APP_ROUTES.AUTH.LOGIN} replace />;
  }

  if (isLoading) {
    return (
      <main className="p-4 md:p-6">
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground">
            <FormattedMessage id="viewings.loading" />
          </CardContent>
        </Card>
      </main>
    );
  }

  if (isError) {
    return (
      <main className="p-4 md:p-6">
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground">
            <FormattedMessage id="viewings.error" />
          </CardContent>
        </Card>
      </main>
    );
  }

  const inspectorRequests = snapshot.assignments
    .filter(isInspectorVisibleAssignment)
    .map((assignment) => toViewingRequest(assignment, session.inspectorId));

  const normalizedSearch = search.trim().toLowerCase();
  const pendingRequests = inspectorRequests.filter((request) =>
    PENDING_OFFER_STATUSES.includes(request.lifecycleStatus),
  );
  const myJobRequests = inspectorRequests.filter((request) =>
    MY_JOB_STATUSES.includes(request.lifecycleStatus),
  );
  const pastJobRequests = inspectorRequests.filter((request) =>
    PAST_JOB_STATUSES.includes(request.lifecycleStatus),
  );
  const activeTabRequests =
    activeTab === "pending"
      ? pendingRequests
      : activeTab === "jobs"
        ? myJobRequests
        : pastJobRequests;
  const searchedRequests = normalizedSearch
    ? activeTabRequests.filter((request) =>
        [
          request.evalionId,
          request.title,
          request.addressLine,
          request.lifecycleStatus,
          request.productType,
          request.inspectionType,
        ]
          .filter(Boolean)
          .some((value) => value?.toLowerCase().includes(normalizedSearch)),
      )
    : activeTabRequests;
  const visibleRequests = searchedRequests.filter((request) =>
    matchesInspectorFilters(request, filters),
  );
  const statusOptions = uniqueSorted(
    activeTabRequests.map((request) => request.lifecycleStatus),
  );
  const inspectionOptions = uniqueSorted(
    activeTabRequests.map((request) => request.inspectionType),
  );
  const productOptions = uniqueSorted(
    activeTabRequests.map((request) => request.productType),
  );
  const selectedRequest = selectedRequestId
    ? snapshot.assignments.find(
        (assignment) =>
          assignment.id === selectedRequestId &&
          isInspectorVisibleAssignment(assignment),
      ) || null
    : null;
  const switchTab = (tab: ViewingsTab) => {
    setActiveTab(tab);
    setSelectedRequestId(null);
    updatePortalSearchParams({
      [URL_PARAM_ASSIGNMENT_TAB]: tab,
      [URL_PARAM_ASSIGNMENT_ID]: undefined,
      [URL_PARAM_ASSIGNMENT_DETAIL_TAB]: undefined,
    });
  };
  const openAssignment = (request: ViewingsDispatchRequest) => {
    setActiveTab(getTabForStatus(request.lifecycleStatus));
    setSelectedRequestId(request.id);
    updatePortalSearchParams({
      [URL_PARAM_ASSIGNMENT_TAB]: getTabForStatus(request.lifecycleStatus),
      [URL_PARAM_ASSIGNMENT_ID]: request.id,
      [URL_PARAM_ASSIGNMENT_DETAIL_TAB]: undefined,
    });
  };

  const handleTabKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    tab: ViewingsTab,
  ) => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
      return;
    }

    event.preventDefault();
    const nextTab =
      event.key === "Home"
        ? "pending"
        : event.key === "End"
          ? "jobs"
          : event.key === "ArrowRight"
            ? tab === "pending"
              ? "jobs"
              : tab === "jobs"
                ? "past"
                : "pending"
            : tab === "past"
              ? "jobs"
              : tab === "jobs"
                ? "pending"
                : "past";
    switchTab(nextTab);
    (nextTab === "pending"
      ? pendingTabRef
      : nextTab === "jobs"
        ? jobsTabRef
        : pastTabRef
    ).current?.focus();
  };

  return (
    <main className="p-4 md:p-6">
      <div className="flex w-full flex-col gap-4">
        <section className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
          <div className="flex flex-col gap-4 border-b border-border p-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-base font-semibold text-primary">
                <FormattedMessage id="viewings.title" />
              </h2>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  aria-label={intl.formatMessage({ id: "viewings.search" })}
                  placeholder={intl.formatMessage({ id: "viewings.search" })}
                  className="h-9 w-full bg-muted-background pl-9 sm:w-72"
                  onChange={(event) => setSearch(event.target.value)}
                />
              </div>
              <InspectorGroupedFilterPopover
                filters={filters}
                statusOptions={statusOptions}
                inspectionOptions={inspectionOptions}
                productOptions={productOptions}
                onApply={setFilters}
                onClear={() => setFilters(EMPTY_INSPECTOR_FILTERS)}
              />
            </div>
          </div>

          <div className="border-b border-border px-4 pt-3">
            <div
              role="tablist"
              aria-label={intl.formatMessage({ id: "viewings.tabs.label" })}
              className="flex flex-wrap gap-2"
            >
              <button
                ref={pendingTabRef}
                type="button"
                role="tab"
                id={TAB_IDS.pending}
                aria-controls={TAB_PANEL_IDS.pending}
                aria-selected={activeTab === "pending"}
                tabIndex={activeTab === "pending" ? 0 : -1}
                className={`inline-flex items-center gap-2 rounded-t-md border border-b-0 px-3 py-2 text-sm font-medium transition-colors ${
                  activeTab === "pending"
                    ? "border-border bg-card text-primary"
                    : "border-transparent text-muted-foreground hover:text-primary"
                }`}
                onClick={() => switchTab("pending")}
                onKeyDown={(event) => handleTabKeyDown(event, "pending")}
              >
                <FormattedMessage id="viewings.tabs.pendingLabel" />
                <span className="rounded-full bg-categories-bright-orange-700 px-2 py-0.5 text-xs font-semibold text-white">
                  ({pendingRequests.length})
                </span>
              </button>
              <button
                ref={jobsTabRef}
                type="button"
                role="tab"
                id={TAB_IDS.jobs}
                aria-controls={TAB_PANEL_IDS.jobs}
                aria-selected={activeTab === "jobs"}
                tabIndex={activeTab === "jobs" ? 0 : -1}
                className={`rounded-t-md border border-b-0 px-3 py-2 text-sm font-medium transition-colors ${
                  activeTab === "jobs"
                    ? "border-border bg-card text-primary"
                    : "border-transparent text-muted-foreground hover:text-primary"
                }`}
                onClick={() => switchTab("jobs")}
                onKeyDown={(event) => handleTabKeyDown(event, "jobs")}
              >
                <FormattedMessage
                  id="viewings.tabs.jobs"
                  values={{ count: myJobRequests.length }}
                />
              </button>
              <button
                ref={pastTabRef}
                type="button"
                role="tab"
                id={TAB_IDS.past}
                aria-controls={TAB_PANEL_IDS.past}
                aria-selected={activeTab === "past"}
                tabIndex={activeTab === "past" ? 0 : -1}
                className={`rounded-t-md border border-b-0 px-3 py-2 text-sm font-medium transition-colors ${
                  activeTab === "past"
                    ? "border-border bg-card text-primary"
                    : "border-transparent text-muted-foreground hover:text-primary"
                }`}
                onClick={() => switchTab("past")}
                onKeyDown={(event) => handleTabKeyDown(event, "past")}
              >
                <FormattedMessage
                  id="viewings.tabs.past"
                  values={{ count: pastJobRequests.length }}
                />
              </button>
            </div>
          </div>

          <div
            id={TAB_PANEL_IDS[activeTab]}
            role="tabpanel"
            aria-labelledby={TAB_IDS[activeTab]}
          >
            <div className="hidden md:block">
              <DataTable
                columns={columns}
                data={visibleRequests}
                noResultsMessage={intl.formatMessage({ id: "viewings.empty" })}
                onRowActivate={openAssignment}
                pageSize={10}
              />
            </div>

            <div className="space-y-3 p-4 md:hidden">
              {visibleRequests.length === 0 ? (
                <Card>
                  <CardContent className="pt-6 text-sm text-muted-foreground">
                    <FormattedMessage id="viewings.empty" />
                  </CardContent>
                </Card>
              ) : null}

              {visibleRequests.map((request) => (
                <ViewingRequestCard
                  key={request.id}
                  request={request}
                  uploadProgress={uploadProgressByRequestId[request.id] || 0}
                  onActivate={openAssignment}
                />
              ))}
            </div>
          </div>
        </section>

        <ViewingDetailSheet
          request={selectedRequest}
          activeInspector={activeInspector}
          inspectorId={session.inspectorId}
          activeDetailTab={activeDetailTab}
          acceptDateInput={acceptDateInput}
          actionErrorMessageId={actionErrorMessageId}
          onOpenChange={(open) => {
            if (!open) {
              closingAssignmentIdRef.current = selectedRequestId;
              updatePortalSearchParams({
                [URL_PARAM_ASSIGNMENT_ID]: undefined,
                [URL_PARAM_ASSIGNMENT_DETAIL_TAB]: undefined,
              });
              setSelectedRequestId(null);
            }
          }}
          onDetailTabChange={(tab) =>
            updatePortalSearchParams({
              [URL_PARAM_ASSIGNMENT_DETAIL_TAB]: tab,
            })
          }
          onAcceptDateInputChange={setAcceptDateInput}
          onUpdateRequest={updateRequest}
          onAcceptOffer={handleAcceptOffer}
          onDeclineOffer={handleDeclineOffer}
          onSubmitViewingDate={handleSubmitViewingDate}
          onUploadFiles={handleUploadFiles}
          onDeleteFile={handleDeleteFile}
        />
      </div>
    </main>
  );
}
